import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  class: "text-primary-dark mb-2 font-weight-semi-bold",
  "data-test-ref": "multi-factor-auth-title"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("hr", null, null, -1 /* HOISTED */)
const _hoisted_3 = {
  class: "d-flex align-items-center",
  "data-test-ref": "member-mfa-faq"
}
const _hoisted_4 = ["href"]

import { onMounted, nextTick, computed, ref, watch, type PropType } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n, mergeMessages } from '@/i18n';
import type { LocaleMessages } from 'vue-i18n';
import { formatPhoneNumber } from '@/utils/format';
import MemberContactChannels from '@/components/mfa/multi-factor-auth-contact-channels.vue';
import MemberCode from '@/components/mfa/multi-factor-auth-code.vue';
import MultiFactorAuthFaqModal from '@/components/mfa/multi-factor-auth-faq-modal.vue';
import * as logger from 'commercial-web-client-utilities/dist/logging/logger';
import { useSiteConfig } from '@/composables/site-config';


export default /*#__PURE__*/_defineComponent({
  __name: 'multi-factor-auth',
  props: {
    i18nMessages: {
        type: Object as PropType<LocaleMessages<unknown>>,
        default: () => ({}),
    }
},
  setup(__props) {

const props = __props;

const i18n = useI18n();
const { t } = i18n;
const route = useRoute();
const router = useRouter();
const phoneNumber = t('mfa.faq.phoneNumber');
const siteConfig = useSiteConfig();
const formattedPhoneNumber = computed(() => formatPhoneNumber(phoneNumber));
const showFaqModal = ref<boolean>(false);

/*-----*\
| Props |
\*-----*/

/*--------*\
| Watchers |
\*--------*/
watch(() => props.i18nMessages, (i18nMessages) => {
    mergeMessages(i18n, i18nMessages, ['mfa']);
}, { immediate: true });

onMounted(async () => {

    try {
        const baseRouteName = router.currentRoute.value.name ?? 'multi-factor-auth';
        const allRoutes = router.getRoutes();
        const existingBaseRoute = allRoutes.find((r) => r.name === baseRouteName);
        const hasOptionsRoute = allRoutes.some((r) => r.name === 'multi-factor-auth-contact-channels');
        const hasCodeRoute = allRoutes.some((r) => r.name === 'multi-factor-auth-code');

        if (!existingBaseRoute) {
            router.addRoute({
                path: '/mfa',
                name: baseRouteName,
                component: { template: '<router-view></router-view>' },
                redirect: { name: 'multi-factor-auth-contact-channels' },
                children: [
                    { path: '', name: 'multi-factor-auth-contact-channels', component: MemberContactChannels },
                    { path: 'code', name: 'multi-factor-auth-code', component: MemberCode },
                ],
            });
        } else {
            if (!hasOptionsRoute) {
                router.addRoute(baseRouteName, {
                    path: '',
                    name: 'multi-factor-auth-contact-channels',
                    component: MemberContactChannels,
                });
            }
            if (!hasCodeRoute) {
                router.addRoute(baseRouteName, {
                    path: 'code',
                    name: 'multi-factor-auth-code',
                    component: MemberCode,
                });
            }
        }

        await nextTick();
        const currentRouteName = route.name;
        if (currentRouteName === baseRouteName || !currentRouteName) {
            await router.replace({ name: 'multi-factor-auth-contact-channels' }).catch((err) =>
                logger.warn('Navigation failed:', err)
            );
        } else if (route.path.includes('/code') && !route.name) {
            await router.replace({ name: 'multi-factor-auth-code', query: route.query }).catch((err) =>
                logger.error('Navigation failed:', err)
            );
        }
    } catch (error) {
        logger.error('Error in MFA setup:', error);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!
  const _component_b_link = _resolveComponent("b-link")!
  const _component_i18n_t = _resolveComponent("i18n-t")!
  const _component_b_container = _resolveComponent("b-container")!
  const _directive_b_modal = _resolveDirective("b-modal")!

  return (_openBlock(), _createBlock(_component_b_container, {
    "data-test-ref": "multi-factor-auth-container",
    class: "py-5 px-2"
  }, {
    default: _withCtx(() => [
      _createElementVNode("h1", _hoisted_1, _toDisplayString(_unref(t)('mfa.title.confirmHeading')), 1 /* TEXT */),
      _hoisted_2,
      _createVNode(_component_router_view, { "data-test-ref": "multi-factor-auth-router-view" }),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_i18n_t, {
          keypath: "mfa.faq.faqPrompt",
          tag: "span",
          scope: "global"
        }, {
          faqLink: _withCtx(() => [
            _withDirectives((_openBlock(), _createBlock(_component_b_link, {
              "data-test-ref": "multi-factor-auth-faq-link",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (showFaqModal.value = true))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('mfa.faq.faqLink')), 1 /* TEXT */)
              ], undefined, true),
              _: 1 /* STABLE */
            })), [
              [_directive_b_modal, 'faqModal']
            ])
          ]),
          technicalSupport: _withCtx(() => [
            _createVNode(_component_b_link, {
              "data-test-ref": "multi-factor-auth-technical-support",
              onClick: _unref(siteConfig).redirectToTechSupport
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('mfa.faq.technicalSupport')), 1 /* TEXT */)
              ], undefined, true),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["onClick"])
          ]),
          phoneNumber: _withCtx(() => [
            _createElementVNode("a", {
              href: `tel:${_unref(formattedPhoneNumber)}`,
              "data-test-ref": "multi-factor-auth-phone-number"
            }, _toDisplayString(_unref(formattedPhoneNumber)), 9 /* TEXT, PROPS */, _hoisted_4)
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _createVNode(MultiFactorAuthFaqModal, {
        id: "faqModal",
        modelValue: showFaqModal.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showFaqModal).value = $event)),
        "hide-footer": true
      }, null, 8 /* PROPS */, ["modelValue"])
    ], undefined, true),
    _: 1 /* STABLE */
  }))
}
}

})